import type { Session, SupabaseClient } from '@supabase/supabase-js'
import type {
  AuthProvider,
  AuthSession,
  NativeOAuthHandlingResult,
  SignInOptions,
} from './authProvider'
import { getSupabaseClient } from '../supabase/client'
import { getSupabaseConfig, type AppEnvironment } from '../supabase/config'
import {
  nativeOAuthAttemptStorage,
  type NativeOAuthAttempt,
} from '../supabase/authStorage'
import { getNativePlatform } from '../utils/nativeRuntime'
import { createNativeOAuthAttempt, parseNativeOAuthCallback } from './nativeOAuth'

type ClientLoader = () => Promise<SupabaseClient>

interface NativeOAuthDependencies {
  isNativeAndroid: () => boolean
  environment: () => AppEnvironment
  readAttempt: () => Promise<NativeOAuthAttempt | null>
  writeAttempt: (attempt: NativeOAuthAttempt) => Promise<void>
  clearAttempt: (expectedId?: string) => Promise<boolean>
  openBrowser: (url: string) => Promise<void>
  closeBrowser: () => Promise<void>
  now: () => number
}

const defaultNativeOAuthDependencies: NativeOAuthDependencies = {
  isNativeAndroid: () => getNativePlatform(window) === 'android',
  environment: () => getSupabaseConfig()?.environment ?? 'development',
  readAttempt: () => nativeOAuthAttemptStorage.read(),
  writeAttempt: (attempt) => nativeOAuthAttemptStorage.write(attempt),
  clearAttempt: (expectedId) => nativeOAuthAttemptStorage.clear(expectedId),
  openBrowser: async (url) => {
    const { Browser } = await import('@capacitor/browser')
    await Browser.open({ url, toolbarColor: '#080b1d' })
  },
  closeBrowser: async () => {
    const { Browser } = await import('@capacitor/browser')
    await Browser.close()
  },
  now: Date.now,
}

function mapSession(session: Session | null): AuthSession | null {
  if (!session) return null
  const metadata = session.user.user_metadata
  const name =
    (typeof metadata.full_name === 'string' && metadata.full_name) ||
    (typeof metadata.name === 'string' && metadata.name) ||
    session.user.email ||
    'miniMany user'
  const avatarUrl =
    (typeof metadata.avatar_url === 'string' && metadata.avatar_url) ||
    (typeof metadata.picture === 'string' && metadata.picture) ||
    null

  return {
    user: {
      id: session.user.id,
      name,
      email: session.user.email ?? null,
      avatarUrl,
    },
    expiresAt: session.expires_at ? session.expires_at * 1000 : null,
  }
}

export class SupabaseGoogleAuthProvider implements AuthProvider {
  readonly id = 'supabase-google'
  private client: SupabaseClient | null = null
  private initialization: Promise<SupabaseClient> | null = null
  private readonly listeners = new Set<(session: AuthSession | null) => void>()

  constructor(
    private readonly loadClient: ClientLoader = getSupabaseClient,
    private readonly nativeOAuth: NativeOAuthDependencies = defaultNativeOAuthDependencies,
  ) {}

  async initialize(): Promise<void> {
    await this.getInitializedClient()
  }

  async signIn(options: SignInOptions = {}): Promise<null> {
    const client = await this.getInitializedClient()
    const queryParams = options.reauthenticate ? { prompt: 'select_account' } : undefined
    if (this.nativeOAuth.isNativeAndroid()) {
      const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
      const attempt = createNativeOAuthAttempt(
        this.nativeOAuth.environment(),
        returnPath,
        this.nativeOAuth.now(),
      )
      await this.nativeOAuth.writeAttempt(attempt)
      const { data, error } = await client.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: attempt.redirectUrl,
          skipBrowserRedirect: true,
          ...(queryParams ? { queryParams } : {}),
        },
      })
      if (error || !data.url) {
        await this.nativeOAuth.clearAttempt(attempt.id)
        if (error) throw error
        throw new Error('Google sign-in did not return a browser URL.')
      }
      try {
        await this.nativeOAuth.openBrowser(data.url)
      } catch (error) {
        await this.nativeOAuth.clearAttempt(attempt.id)
        throw error
      }
      return null
    }
    const redirectTo = `${window.location.origin}${window.location.pathname}`
    const { error } = await client.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo, ...(queryParams ? { queryParams } : {}) },
    })
    if (error) throw error
    return null
  }

  async signOut(): Promise<void> {
    const client = await this.getInitializedClient()
    await this.nativeOAuth.clearAttempt()
    const { error } = await client.auth.signOut()
    if (error) throw error
    this.setSession(null)
  }

  async getSession(): Promise<AuthSession | null> {
    return this.readClientSession(await this.getInitializedClient())
  }

  onSessionChange(listener: (session: AuthSession | null) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  async handleNativeOAuthCallback(rawUrl: string): Promise<NativeOAuthHandlingResult> {
    if (!this.nativeOAuth.isNativeAndroid()) return { status: 'ignored' }
    const attempt = await this.nativeOAuth.readAttempt()
    if (!attempt) return { status: 'ignored' }
    const callback = parseNativeOAuthCallback(
      rawUrl,
      this.nativeOAuth.environment(),
      attempt,
      this.nativeOAuth.now(),
    )
    if (callback.kind === 'invalid') {
      if (callback.reason === 'expired') {
        await this.nativeOAuth.clearAttempt(attempt.id)
        await this.closeNativeBrowser()
        return { status: 'canceled' }
      }
      return { status: 'ignored' }
    }
    if (callback.kind === 'provider-error') {
      await this.nativeOAuth.clearAttempt(attempt.id)
      await this.closeNativeBrowser()
      return { status: 'canceled' }
    }
    await this.nativeOAuth.writeAttempt({ ...attempt, authorizationCode: callback.code })
    return this.exchangeNativeOAuthCode(attempt.id, callback.code)
  }

  async resumeNativeOAuthCallback(): Promise<NativeOAuthHandlingResult> {
    if (!this.nativeOAuth.isNativeAndroid()) return { status: 'ignored' }
    const attempt = await this.nativeOAuth.readAttempt()
    if (!attempt) return { status: 'ignored' }
    if (this.nativeOAuth.now() > attempt.expiresAt) {
      await this.nativeOAuth.clearAttempt(attempt.id)
      await this.closeNativeBrowser()
      return { status: 'canceled' }
    }
    if (!attempt.authorizationCode) return { status: 'ignored' }
    const existing = await this.readClientSession(await this.getInitializedClient())
    if (existing) {
      await this.nativeOAuth.clearAttempt(attempt.id)
      await this.closeNativeBrowser()
      return { status: 'success', session: existing }
    }
    return this.exchangeNativeOAuthCode(attempt.id, attempt.authorizationCode)
  }

  async cancelPendingNativeOAuth(): Promise<boolean> {
    if (!this.nativeOAuth.isNativeAndroid()) return false
    const attempt = await this.nativeOAuth.readAttempt()
    if (!attempt || attempt.authorizationCode) return false
    return this.nativeOAuth.clearAttempt(attempt.id)
  }

  private getInitializedClient(): Promise<SupabaseClient> {
    if (this.client) return Promise.resolve(this.client)
    if (this.initialization) return this.initialization
    const attempt = this.loadClient().then((client) => {
      client.auth.onAuthStateChange((_event, session) => this.setSession(mapSession(session)))
      this.client = client
      return client
    })
    this.initialization = attempt
    void attempt.catch(() => {
      if (this.initialization === attempt) this.initialization = null
    })
    return attempt
  }

  private async readClientSession(client: SupabaseClient): Promise<AuthSession | null> {
    const { data, error } = await client.auth.getSession()
    if (error) throw error
    const session = mapSession(data.session)
    this.setSession(session)
    return session
  }

  private async exchangeNativeOAuthCode(
    attemptId: string,
    authorizationCode: string,
  ): Promise<NativeOAuthHandlingResult> {
    const client = await this.getInitializedClient()
    const { data, error } = await client.auth.exchangeCodeForSession(authorizationCode)
    if (error) throw new Error('Unable to complete Google sign-in. Reconnect and try again.')
    const session = mapSession(data.session)
    if (!session) throw new Error('Google sign-in completed without a session.')
    await this.nativeOAuth.clearAttempt(attemptId)
    await this.closeNativeBrowser()
    this.setSession(session)
    return { status: 'success', session }
  }

  private async closeNativeBrowser(): Promise<void> {
    await this.nativeOAuth.closeBrowser().catch(() => undefined)
  }

  private setSession(session: AuthSession | null): void {
    for (const listener of this.listeners) listener(session)
  }
}
